import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { useDrag, useDrop } from 'react-dnd';

import confirm from 'components/confirm';
import Modal from './modal';
import { deleteRequest } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { getCategoryLabel, getClassFlagLabels, getRecordCategoryLabels, getTypeLabel } from './helpers';

const type = 'CUSTOM_FIELD';

export default function CustomField({
  customField,
  customFields,
  features,
  options,
  paths,
  onChange,
  onDelete,
  onDragHover,
  onDrop,
}) {
  const [showModal, setShowModal] = useState(false);

  const ref = useRef(null);
  const dragRef = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    item: customField,
    type,
  });

  const [, drop] = useDrop({
    accept: type,
    hover(draggedCustomField) {
      if (customField.name !== draggedCustomField.name) {
        onDragHover(draggedCustomField.name, customField.name);
      }
    },
    drop() {
      onDrop();
    },
  });

  preview(drop(ref));
  drag(dragRef);

  async function destroy() {
    try {
      await confirm(`
        Are you sure you want to delete this custom field?
        Any data entered into this field on deals will be lost.
      `, {
        buttonLabel: 'Delete',
        buttonStyle: 'danger',
        title: `Confirm deletion`
      });
    } catch (error) {
      if (error !== undefined) {
        throw error;
      }
      return;
    }

    try {
      await deleteRequest(paths.customFields, { params: { name: customField.name } });
    } catch (errors) {
      showSnackbar({
        text: requestErrors(errors, 'The custom field could not be deleted. Please try again.').join(' '),
        variant: 'danger',
      });
      return;
    }

    onDelete(customField);
  }

  return (
    <tr key={customField.name} ref={ref} style={{ opacity: isDragging ? 0 : 1 }}>
      <td ref={dragRef} style={{ cursor: 'grab', width: 0 }}>
        <FontAwesomeIcon className="text-muted" icon={faGripVertical}/>
      </td>
      <td>
        <a className="clickable" onClick={() => setShowModal(true)}>
          {customField.label}
        </a>

        <Modal
          customField={customField}
          customFields={customFields}
          features={features}
          options={options}
          paths={paths}
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={onChange}
        />
      </td>
      {features.edge && <td>{getClassFlagLabels(customField).join(', ')}</td>}
      <td>{getTypeLabel(customField, options)}</td>
      <td>{getRecordCategoryLabels(customField, options)}</td>
      <td>{getCategoryLabel(customField, options)}</td>
      {features.marketingIntegration &&
        <td>{customField.marketing?.type} {customField.marketing?.field}</td>
      }
      <td className="align-middle">
        <FontAwesomeIcon className="clickable text-destructive" icon={faTrashAlt} onClick={destroy} />
      </td>
    </tr>
  );
}

CustomField.propTypes = {
  customField: PropTypes.shape({
    category: PropTypes.string,
    dealTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    label: PropTypes.string.isRequired,
    marketing: PropTypes.shape({
      type: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
    }),
    type: PropTypes.string.isRequired,
  }).isRequired,
  customFields: PropTypes.array,
  features: PropTypes.shape({
    marketingIntegration: PropTypes.bool,
  }).isRequired,
  options: PropTypes.object.isRequired,
  paths: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDragHover: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
};
